/**
 * useSlashCommands — parse the chat composer text against the v1 slash-command
 * registry and return autosuggest rows.
 *
 * v1 commands (per FRONTEND_PLAN.md §11 + the HTML walkthrough §04 composer):
 *   /workflow run <name>
 *   /memory search <query>
 *   /connect <tool>
 *   /help
 *
 * Pure parser. No I/O. The composer wires these into Cloudscape's
 * `Autosuggest` and renders the rows via `SlashCommandSuggestion` (S1).
 */

import { useMemo } from 'react';
import type { SlashCommandValidation } from '@/components/atoms/SlashCommandSuggestion';

export interface SlashCommandSpec {
  /** Full command, e.g. `/workflow run`. */
  readonly command: string;
  /** Argument shape, e.g. `<name>`. */
  readonly args?: string;
  /** Plain-prose description for the dropdown row. */
  readonly description: string;
  /** Validate the argument string. Returns null when valid. */
  readonly validate?: (rawArgs: string) => string | null;
}

export interface SlashSuggestion {
  readonly command: string;
  readonly args?: string;
  readonly description: string;
  readonly validation?: SlashCommandValidation;
  readonly error?: string;
  /** What the composer should fill in when this row is accepted. */
  readonly fill: string;
}

const NON_EMPTY = (rawArgs: string): string | null =>
  rawArgs.trim().length > 0 ? null : 'Argument required';

const TOOL_TOKEN = /^[a-z][a-z0-9-]{0,40}$/i;
const validateConnect = (rawArgs: string): string | null => {
  const trimmed = rawArgs.trim();
  if (trimmed.length === 0) return 'Tool name required';
  return TOOL_TOKEN.test(trimmed) ? null : 'Use lowercase letters, digits, hyphens';
};

export const SLASH_COMMANDS: readonly SlashCommandSpec[] = [
  {
    command: '/workflow run',
    args: '<name>',
    description: 'Run a workflow by name.',
    validate: NON_EMPTY,
  },
  {
    command: '/memory search',
    args: '<query>',
    description: "Search memory for matching items.",
    validate: NON_EMPTY,
  },
  {
    command: '/remember',
    args: '<text>',
    description: 'Save a fact to memory.',
    validate: NON_EMPTY,
  },
  {
    command: '/connect',
    args: '<tool>',
    description: 'Open the consent flow to grant a downstream service.',
    validate: validateConnect,
  },
  {
    command: '/help',
    description: 'List slash commands and keyboard shortcuts.',
  },
];

/**
 * Split the input into `(command, rest)` if it starts with `/`. The split
 * is greedy: we match the longest registered command prefix so
 * `/workflow run foo` resolves to `/workflow run` + `foo`.
 */
export function splitCommand(
  input: string,
): { command: string; rest: string } | null {
  if (!input.startsWith('/')) return null;
  // Try the longest prefix first.
  const sorted = [...SLASH_COMMANDS]
    .map((c) => c.command)
    .sort((a, b) => b.length - a.length);
  for (const cmd of sorted) {
    if (input === cmd) return { command: cmd, rest: '' };
    if (input.startsWith(cmd + ' ')) {
      return { command: cmd, rest: input.slice(cmd.length + 1) };
    }
  }
  // Bare `/foo` — treat as a partial command for filtering.
  return { command: input, rest: '' };
}

/**
 * Compute autosuggest rows for the given input. Returns an empty array if
 * the input does not start with `/`.
 */
export function getSuggestions(input: string): SlashSuggestion[] {
  if (!input.startsWith('/')) return [];
  const split = splitCommand(input);
  if (!split) return [];

  // Exact / prefix match against a known command.
  const exact = SLASH_COMMANDS.find((c) => c.command === split.command);
  if (exact) {
    const rawArgs = split.rest;
    const error = exact.validate ? exact.validate(rawArgs) : null;
    const validation: SlashCommandValidation | undefined = exact.validate
      ? rawArgs.length === 0
        ? 'pending'
        : error
          ? 'invalid'
          : 'valid'
      : undefined;
    // Only surface the error when validation === 'invalid'. The 'pending'
    // state is "waiting for input" — not "the user is wrong".
    const showError = validation === 'invalid' && error !== null;
    return [
      {
        command: exact.command,
        ...(exact.args !== undefined ? { args: exact.args } : {}),
        description: exact.description,
        ...(validation !== undefined ? { validation } : {}),
        ...(showError && error !== null ? { error } : {}),
        fill: exact.command + (exact.args ? ' ' : ''),
      },
    ];
  }

  // Filter the registry by prefix on the typed (incomplete) command.
  const typed = split.command.toLowerCase();
  return SLASH_COMMANDS.filter((c) => c.command.toLowerCase().startsWith(typed)).map(
    (c) => ({
      command: c.command,
      ...(c.args !== undefined ? { args: c.args } : {}),
      description: c.description,
      fill: c.command + (c.args ? ' ' : ''),
    }),
  );
}

/**
 * React hook over the parser. `useMemo`'d so the suggestion list is stable
 * between renders when the input does not change.
 */
export function useSlashCommands(input: string): {
  suggestions: SlashSuggestion[];
  invalid: boolean;
} {
  return useMemo(
    () => ({
      suggestions: getSuggestions(input),
      invalid: isSlashCommandInvalid(input),
    }),
    [input],
  );
}

/**
 * Returns true if the composer's current input is a slash command that is
 * either incomplete or invalid — used to disable the send button.
 */
export function isSlashCommandInvalid(input: string): boolean {
  if (!input.startsWith('/')) return false;
  const split = splitCommand(input);
  if (!split) return true;
  const exact = SLASH_COMMANDS.find((c) => c.command === split.command);
  if (!exact) return true;
  if (!exact.validate) return false;
  return exact.validate(split.rest) !== null;
}
